import React, { useRef, useEffect, useCallback } from "react";
import { Input } from "antd";
import { TextAreaProps } from "antd/lib/input";
import { getMobileOS } from "../../services";
import { MobileOS } from "../../models";
import styles from "./styles.scss";

interface AutoResizeTextAreaProps extends TextAreaProps {
  // All TextAreaProps from Antd are supported
}

/**
 * A wrapper around Antd's Input.TextArea that includes iOS-specific fixes
 * for scrolling issues when using autoSize prop.
 *
 * This component automatically handles the iOS bug where the textarea doesn't
 * scroll properly when text is deleted or when the textarea auto-resizes.
 */
export const AutoResizeTextArea = React.forwardRef(
  (props: AutoResizeTextAreaProps, forwardedRef) => {
    const { onChange, onPaste, onCut, onDrop, autoSize, ...restProps } = props;
    const internalRef = useRef(null);

    // Merge the forwarded ref with internal ref
    const setRefs = useCallback((node) => {
      // Set internal ref
      internalRef.current = node;

      // Set forwarded ref if it exists
      if (forwardedRef) {
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef.current !== undefined) {
          forwardedRef.current = node;
        }
      }
    }, [forwardedRef]);

    const applyIOSScrollFix = useCallback(() => {
      // Apply iOS fix only if autoSize is enabled
      if (autoSize && getMobileOS() === MobileOS.iOS) {
        // Small delay to ensure DOM has updated after autoSize
        setTimeout(() => {
          const ref = internalRef.current;
          if (ref && ref.resizableTextArea && ref.resizableTextArea.textArea) {
            const textarea = ref.resizableTextArea.textArea;
            // Only jump to the end when the cursor is actually at the end
            // (typical when appending). Forcing scrollTop when editing in the
            // middle would push the cursor line out of view — the "ekran
            // poskoči" symptom users reported on iOS.
            const isCursorAtEnd = textarea.selectionEnd >= textarea.value.length;
            if (isCursorAtEnd) {
              textarea.scrollTop = textarea.scrollHeight;
            }
          }
        }, 0);
      }
    }, [autoSize]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      // Call the original onChange handler if provided
      if (onChange) {
        onChange(e);
      }

      applyIOSScrollFix();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Call the original onPaste handler if provided
      if (onPaste) {
        onPaste(e);
      }

      applyIOSScrollFix();
    };

    const handleCut = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Call the original onCut handler if provided
      if (onCut) {
        onCut(e);
      }

      applyIOSScrollFix();
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
      // Call the original onDrop handler if provided
      if (onDrop) {
        onDrop(e);
      }

      applyIOSScrollFix();
    };

    return (
      <Input.TextArea
        ref={setRefs}
        className={`${styles.autoResizeTextArea} ${props.className || ''}`}
        onChange={handleChange}
        onPaste={handlePaste}
        onCut={handleCut}
        onDrop={handleDrop}
        autoSize={autoSize}
        {...restProps}
      />
    );
  }
);

AutoResizeTextArea.displayName = 'AutoResizeTextArea';



// WEBPACK FOOTER //
// ./src/components/AutoResizeTextArea/index.tsx